(() => {
  const SELECTORS = {
    root: '[data-fe-premium-pdp="true"]',
    form: 'form[data-type="add-to-cart-form"]',
    submit: '[id^="ProductSubmitButton-"]',
    submitText: '[id^="ProductSubmitButton-"] span',
    sticky: '[data-fe-pdp-sticky]',
    stickyButton: '[data-fe-pdp-sticky-submit]',
    stickyPrice: '[data-fe-pdp-sticky-price]',
    price: '[id^="price-"] .price-item--sale, [id^="price-"] .price-item--regular',
    purchaseData: '[data-fe-pdp-purchase-data]',
    planData: '[data-fe-pdp-plan-data]',
    valueDelivery: '[data-fe-pdp-value-delivery]',
    valueSave: '[data-fe-pdp-value-save]',
  };

  const getSellingPlanInput = (form) => form?.querySelector('[name="selling_plan"]');

  const isSubscriptionSelected = (root, form) => {
    const input = getSellingPlanInput(form);
    if (input) return Boolean(input.value) && !input.disabled;
    return root.dataset.feHasSellingPlans === 'true' && root.dataset.feDefaultPurchase === 'subscription';
  };

  const getSelectedPlanData = (root) => {
    const form = root.querySelector(SELECTORS.form);
    const selectedPlanId = getSellingPlanInput(form)?.value || '';
    const purchaseData = root.querySelector(SELECTORS.purchaseData);
    const planData = Array.from(root.querySelectorAll(SELECTORS.planData));
    const selectedPlan = planData.find((plan) => plan.dataset.sellingPlanId === selectedPlanId) || planData[0];

    return {
      discount: selectedPlan?.dataset.planDiscount || '',
      label: selectedPlan?.dataset.planLabel || '',
      price: selectedPlan?.dataset.planPrice || purchaseData?.dataset.subscriptionPrice || '',
      sellingPlanId: selectedPlan?.dataset.sellingPlanId || selectedPlanId || purchaseData?.dataset.subscriptionPlanId || '',
    };
  };

  const syncValueStack = (root, subscriptionSelected) => {
    const purchaseData = root.querySelector(SELECTORS.purchaseData);
    const selectedPlan = getSelectedPlanData(root);
    const delivery = root.querySelector(SELECTORS.valueDelivery);
    const save = root.querySelector(SELECTORS.valueSave);

    if (subscriptionSelected) {
      if (delivery && selectedPlan.label && delivery.textContent !== selectedPlan.label) delivery.textContent = selectedPlan.label;
      if (save && selectedPlan.discount && save.textContent !== `Save ${selectedPlan.discount}%`) save.textContent = `Save ${selectedPlan.discount}%`;
      return;
    }

    if (purchaseData) {
      const currentPrice = purchaseData.dataset.onetimePrice || '';
      if (purchaseData.dataset.currentPrice !== currentPrice) purchaseData.dataset.currentPrice = currentPrice;
    }
  };

  const setSelectedVisuals = (root, purchaseType, planId) => {
    const subscriptionSelected = purchaseType === 'subscription';
    root.classList.toggle('fe-pdp-subscription-selected', subscriptionSelected);
    root.classList.toggle('fe-pdp-onetime-selected', !subscriptionSelected);
    syncValueStack(root, subscriptionSelected);
  };

  const syncPurchaseState = (root, options = {}) => {
    const form = root.querySelector(SELECTORS.form);
    const subscriptionSelected = isSubscriptionSelected(root, form);
    const submitText = root.querySelector(SELECTORS.submitText);
    const stickyButton = root.querySelector(SELECTORS.stickyButton);
    const purchaseData = root.querySelector(SELECTORS.purchaseData);
    const selectedPlan = getSelectedPlanData(root);
    const price = subscriptionSelected
      ? selectedPlan.price || purchaseData?.dataset.subscriptionPrice
      : purchaseData?.dataset.onetimePrice;

    if (options.updateVisuals !== false) {
      setSelectedVisuals(root, subscriptionSelected ? 'subscription' : 'onetime', selectedPlan.sellingPlanId);
    }

    const label = subscriptionSelected ? 'Start Subscription' : 'Add to Cart';
    if (submitText && submitText.textContent !== label) submitText.textContent = label;
    if (stickyButton && stickyButton.textContent !== label) stickyButton.textContent = label;
    if (purchaseData && price && purchaseData.dataset.currentPrice !== price) purchaseData.dataset.currentPrice = price;
  };

  const syncStickyPrice = (root) => {
    const stickyPrice = root.querySelector(SELECTORS.stickyPrice);
    const price = root.querySelector(SELECTORS.price);
    const purchaseData = root.querySelector(SELECTORS.purchaseData);
    if (stickyPrice && purchaseData?.dataset.currentPrice) {
      stickyPrice.textContent = purchaseData.dataset.currentPrice;
      return;
    }
    if (stickyPrice && price) stickyPrice.textContent = price.textContent.trim();
  };

  const setupStickyCta = (root) => {
    const sticky = root.querySelector(SELECTORS.sticky);
    const stickyButton = root.querySelector(SELECTORS.stickyButton);
    const submit = root.querySelector(SELECTORS.submit);
    const buttons = root.querySelector('.product-form__buttons');
    if (!sticky || !stickyButton || !submit || !buttons) return;

    stickyButton.addEventListener('click', () => submit.click());

    const updateVisibility = () => {
      const rootRect = root.getBoundingClientRect();
      const buttonsRect = buttons.getBoundingClientRect();
      const shouldShow = buttonsRect.bottom < 0 && rootRect.bottom > window.innerHeight + 80;
      sticky.hidden = false;
      sticky.classList.toggle('is-visible', shouldShow);
    };

    window.addEventListener('scroll', updateVisibility, { passive: true });
    window.addEventListener('resize', updateVisibility);
    updateVisibility();
  };

  const observePurchaseChanges = (root) => {
    const form = root.querySelector(SELECTORS.form);
    const appBlock = root.querySelector('.fe-premium-pdp__app-block');
    const update = () => {
      syncPurchaseState(root);
      syncStickyPrice(root);
    };

    root.addEventListener('change', update);
    root.addEventListener('click', () => {
      window.setTimeout(update, 80);
      window.setTimeout(update, 350);
    });

    if (form) {
      new MutationObserver(update).observe(form, {
        attributes: true,
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    if (appBlock) {
      new MutationObserver(update).observe(appBlock, {
        attributes: true,
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    update();
  };

  const init = (root) => {
    if (root.dataset.fePremiumPdpInitialized === 'true') return;
    root.dataset.fePremiumPdpInitialized = 'true';
    observePurchaseChanges(root);
    setupStickyCta(root);
  };

  const initAll = (container = document) => {
    container.querySelectorAll(SELECTORS.root).forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
